import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OuigooLocationPicker } from '@ouigoo/location-picker/react';
import { FaLocationArrow } from 'react-icons/fa';
import { trackPurchase } from '../utils/meta-pixel';
import { getAttribution, getQuoteSessionId, resetQuoteSession } from '../utils/attribution';
import { logQuoteLead } from '../utils/quote-lead';
import { normalizePhone } from '../utils/phone';

const MAX_ORDER_IMAGES = 10;
const MAX_IMAGE_DIMENSION = 900; // mirror mobile catalogue (maxWidth/maxHeight 900)
const IMAGE_QUALITY = 0.78; // mirror mobile catalogue (imageQuality 78)

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Device-local memory of the customer's last delivery info, so a returning
// customer doesn't retype name / phone / address. No account, no backend —
// scoped to this browser only. Order-specific fields (articles, comment,
// images) are intentionally NOT stored.
const PREFILL_STORAGE_KEY = 'ouigoo_order_prefill';

const loadPrefill = () => {
  try {
    const raw = localStorage.getItem(PREFILL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

// Downscale an image file client-side before upload: cap the longest side at
// MAX_IMAGE_DIMENSION and re-encode as JPEG. Falls back to the original file
// if anything goes wrong (e.g. unsupported format).
const downscaleImage = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { width, height } = img;
      const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height));
      // Already small enough → keep original.
      if (scale >= 1) {
        resolve(file);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        IMAGE_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    img.src = objectUrl;
  });

const PublicOrderForm = () => {
  const { businessUid } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1: Info, 2: Location
  const [deliveryOption, setDeliveryOption] = useState('asap'); // 'asap' or 'scheduled'
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [businessName, setBusinessName] = useState('Ouigoo');
  const [formConfig, setFormConfig] = useState(null);
  const [estimation, setEstimation] = useState(null); // { tariff, distance, duration }
  const [isEstimating, setIsEstimating] = useState(false);
  const [businessError, setBusinessError] = useState('');
  const [isBusinessLoading, setIsBusinessLoading] = useState(true);
  const [images, setImages] = useState([]); // uploaded image URLs
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef(null);
  const countryOptions = [
    { code: '+225', label: 'Côte d\'Ivoire', iso: 'CI' },
    { code: '+228', label: 'Togo', iso: 'TG' },
    { code: '+229', label: 'Bénin', iso: 'BJ' },
    { code: '+223', label: 'Mali', iso: 'ML' },
    { code: '+221', label: 'Sénégal', iso: 'SN' },
    { code: '+226', label: 'Burkina Faso', iso: 'BF' },
    { code: '+224', label: 'Guinée', iso: 'GN' },
    { code: '+245', label: 'Guinée-Bissau', iso: 'GW' },
    { code: '+227', label: 'Niger', iso: 'NE' },
    { code: '+234', label: 'Nigéria', iso: 'NG' },
    { code: '+220', label: 'Gambie', iso: 'GM' },
    { code: '+233', label: 'Ghana', iso: 'GH' },
  ];
  const prefixByIso = countryOptions.reduce((acc, item) => {
    acc[item.iso] = item.code;
    return acc;
  }, {});
  const isoByPrefix = countryOptions.reduce((acc, item) => {
    acc[item.code] = item.iso;
    return acc;
  }, {});
  const [selectedCountryCode, setSelectedCountryCode] = useState(() => loadPrefill()?.countryCode || '+229');
  const [formData, setFormData] = useState(() => {
    const saved = loadPrefill();
    return {
      customer_name: saved?.customer_name || '',
      phone: saved?.phone || '',
      instructions: saved?.instructions || '',
      articles: '',
      comment: '',
      address: saved?.address || '',
      lat: saved?.lat ?? null,
      lng: saved?.lng ?? null,
      delivery_date: '',
      delivery_time: ''
    };
  });
  const [hasPrefill, setHasPrefill] = useState(() => !!loadPrefill());
  const [errors, setErrors] = useState({});

  // Fetch form config (includes businessName)
  useEffect(() => {
    const fetchForm = async () => {
      if (!businessUid) return;
      try {
        const apiBase = process.env.REACT_APP_BUSINESS_API_ENDPOINT;
        const params = new URLSearchParams(window.location.search);
        const formId = params.get('form') || '0';
        const res = await fetch(`${apiBase}/v1/public/business/${businessUid}/form?formId=${formId}`);
        if (!res.ok) {
          setBusinessError('Business introuvable ou inaccessible.');
          setIsBusinessLoading(false);
          return;
        }
        const data = await res.json();
        setFormConfig(data);
        if (data?.businessName) setBusinessName(data.businessName);
        setBusinessError('');
        setIsBusinessLoading(false);
      } catch (err) {
        console.error('Form fetch error', err);
        setBusinessError('Erreur lors du chargement du formulaire.');
        setIsBusinessLoading(false);
      }
    };
    fetchForm();
  }, [businessUid]);

  // Geo-detect phone prefix by IP (best-effort). Skip when we already restored
  // a saved prefix from a previous order — the customer's own choice wins.
  useEffect(() => {
    if (loadPrefill()?.countryCode) return;
    const detectCountry = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        if (!res.ok) return;
        const data = await res.json();
        const iso = data?.country_code;
        const prefix = iso && prefixByIso[iso];
        if (prefix) {
          setSelectedCountryCode(prefix);
        }
      } catch (err) {
        console.error('IP detection error', err);
      }
    };
    detectCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const clearPrefill = () => {
    try { localStorage.removeItem(PREFILL_STORAGE_KEY); } catch (err) { /* ignore */ }
    setFormData(prev => ({
      ...prev,
      customer_name: '',
      phone: '',
      address: '',
      lat: null,
      lng: null,
      instructions: '',
    }));
    setSelectedCountryCode('+229');
    setHasPrefill(false);
  };

  const handleImagesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file
    if (files.length === 0) return;

    const remaining = MAX_ORDER_IMAGES - images.length;
    const toUpload = files.slice(0, Math.max(0, remaining));
    if (toUpload.length === 0) return;

    const apiBase = process.env.REACT_APP_BUSINESS_API_ENDPOINT;
    setUploadingCount(c => c + toUpload.length);
    await Promise.all(toUpload.map(async (rawFile) => {
      try {
        const file = await downscaleImage(rawFile);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('business_id', businessUid);
        const res = await fetch(`${apiBase}/v1/public/order/upload`, { method: 'POST', body: fd });
        if (res.ok) {
          const data = await res.json();
          if (data?.url) {
            setImages(prev => (prev.length < MAX_ORDER_IMAGES ? [...prev, data.url] : prev));
            setErrors(prev => ({ ...prev, images: '' }));
          }
        }
      } catch (err) {
        console.error('Image upload error', err);
      } finally {
        setUploadingCount(c => Math.max(0, c - 1));
      }
    }));
  };

  const removeImage = (url) => {
    setImages(prev => prev.filter(u => u !== url));
  };

  const handleLocationSelect = async (result) => {
    const dlat = result.location?.lat;
    const dlng = result.location?.lng;
    setFormData(prev => ({ ...prev, address: result.address, lat: dlat, lng: dlng }));
    if (errors.address) setErrors(prev => ({ ...prev, address: '' }));
    handleClosePicker();

    if (formConfig?.estimationEnabled && formConfig?.pickupAddress && dlat && dlng) {
      try {
        setIsEstimating(true);
        setEstimation(null);
        const apiBase = process.env.REACT_APP_API_ENDPOINT;
        const res = await fetch(`${apiBase}/v1/public/tariff/simulate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deliveryPoints: [{
              referenceId: result.address,
              plat: formConfig.pickupAddress.lat,
              plng: formConfig.pickupAddress.lng,
              dlat,
              dlng,
            }],
            rideTypeId: 1,
            productTypeId: 1,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setEstimation({
            tariff: data.grandTotalTariff,
            distance: data.grandTotalDistance,
            duration: data.grandTotalDuration,
          });
          // Capture du lead de devis (donnée fraîche, l'état estimation n'est pas encore à jour).
          captureLead({ tariff: data.grandTotalTariff, distance: data.grandTotalDistance });
        }
      } catch (err) {
        console.error('Estimation error', err);
      } finally {
        setIsEstimating(false);
      }
    }
  };

  const handleCountryChange = (e) => {
    const code = e.target.value;
    setSelectedCountryCode(code);
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handlePhoneInput = (e) => {
    const raw = e.target.value;
    // Ensure prefix stays and allow digits/spaces
    const sanitized = raw.replace(/[^0-9+\s]/g, '');
    setFormData(prev => ({ ...prev, phone: sanitized }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
    scheduleLeadCapture();
  };

  useEffect(() => {
    if (!showLocationPicker) return;
    window.history.pushState({ locationPicker: true }, '');
    const handlePopState = () => setShowLocationPicker(false);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showLocationPicker]);

  const handleLocationClick = () => {
    // Open picker without pre-validating name/phone; validation happens on submit
    setShowLocationPicker(true);
  };

  const handleClosePicker = () => {
    // If we pushed a history state, go back to pop it; otherwise just close
    if (window.history.state?.locationPicker) {
      window.history.back();
    } else {
      setShowLocationPicker(false);
    }
  };

  // Capture progressive du lead de devis (fire-and-forget) : trajet + tarif + contact client.
  const captureLead = (est) => {
    const e = est || estimation;
    const phoneCountry = selectedCountryCode === '+228' ? 'Togo' : 'Benin';
    const phone = formData.phone
      ? (normalizePhone(formData.phone, phoneCountry) || selectedCountryCode + formData.phone.replace(/\s+/g, ''))
      : '';
    logQuoteLead({
      name: formData.customer_name || '',
      phone,
      pickup_address: formConfig?.pickupAddress?.address || '',
      pickup_lat: formConfig?.pickupAddress?.lat || 0,
      pickup_lng: formConfig?.pickupAddress?.lng || 0,
      delivery_address: formData.address || '',
      delivery_lat: formData.lat || 0,
      delivery_lng: formData.lng || 0,
      tariff: e?.tariff || 0,
      distance: e?.distance || 0,
    });
  };

  // Capture débouncée à la saisie du téléphone (fiable sur mobile où le blur est capricieux).
  const leadTimerRef = useRef(null);
  const scheduleLeadCapture = () => {
    if (leadTimerRef.current) clearTimeout(leadTimerRef.current);
    leadTimerRef.current = setTimeout(() => { captureLead(); }, 900);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'Veuillez saisir votre nom complet';
    const phoneTrim = formData.phone.trim();
    const phonePattern = /^[0-9]{6,15}$/; // digits only, no prefix needed
    if (!phoneTrim) {
      newErrors.phone = 'Veuillez saisir votre numéro de téléphone';
    } else if (!phonePattern.test(phoneTrim.replace(/\s+/g, ''))) {
      newErrors.phone = 'Format invalide. Ex: 01020304';
    }
    if (!formData.address) newErrors.address = 'Veuillez sélectionner une adresse de livraison';
    if (!formData.instructions.trim()) newErrors.instructions = 'Veuillez préciser les détails de l\'adresse';
    if (!formData.articles.trim()) newErrors.articles = 'Veuillez indiquer ce que vous souhaitez commander';
    if (formConfig?.commentRequired && !formData.comment.trim()) {
      newErrors.comment = formConfig?.commentPrompt
        ? `Veuillez remplir : ${formConfig.commentPrompt}`
        : 'Veuillez remplir le commentaire';
    }
    if (formConfig?.imagesEnabled && formConfig?.imagesRequired && images.length === 0) {
      newErrors.images = 'Veuillez ajouter au moins une image';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const formIdParam = new URLSearchParams(window.location.search).get('form') || '0';

    const finalData = {
      business_id: businessUid,
      form_id: Number(formIdParam) || 0,
      customer_name: formData.customer_name,
      // Numéro normalisé E.164 (repli sur préfixe + saisie si non normalisable).
      customer_phone: normalizePhone(formData.phone, selectedCountryCode === '+228' ? 'Togo' : 'Benin')
        || selectedCountryCode + formData.phone.replace(/\s+/g, ''),
      delivery_address: formData.address,
      delivery_lat: formData.lat || 0,
      delivery_lng: formData.lng || 0,
      instructions: formData.instructions,
      articles: formData.articles,
      comment: formData.comment,
      images: images,
      delivery_option: deliveryOption,
      delivery_date: formData.delivery_date,
      delivery_time: formData.delivery_time,
      attribution: { ...getAttribution(), session_id: getQuoteSessionId() },
    };

    try {
      setIsSubmitting(true);
      const apiUrl = `${process.env.REACT_APP_BUSINESS_API_ENDPOINT}/v1/public/order/create`;
      
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalData),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || errorData.detail || 'Erreur lors de la création de la commande';
        console.error(`Server error (${res.status}):`, JSON.stringify(errorData));
        throw new Error(`[${res.status}] ${errorMessage}`);
      }

      const data = await res.json();
      // Remember this customer's delivery info on their device for next time.
      try {
        localStorage.setItem(PREFILL_STORAGE_KEY, JSON.stringify({
          customer_name: formData.customer_name,
          countryCode: selectedCountryCode,
          phone: formData.phone,
          address: formData.address,
          lat: formData.lat,
          lng: formData.lng,
          instructions: formData.instructions,
        }));
      } catch (err) { /* ignore storage errors (private mode, quota) */ }
      setOrderResult(data);
      // Conversion : commande de livraison passée via le formulaire de la vitrine.
      trackPurchase({ value: estimation?.tariff, orderId: data.trackingID || data.trackingId });
      // Lot 1 : la ligne devient convertie -> tourner le session_id pour que la prochaine commande
      // (même onglet, ex. dropshipper) crée une nouvelle ligne au lieu d'écraser celle-ci.
      resetQuoteSession();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      console.error('Submission error', e);
      alert(e.message || "Une erreur est survenue lors de l'envoi de votre commande. Veuillez réessayer.");
      setIsSubmitting(false);
    }
  };

  if (showLocationPicker) {
    return (
      <div
        className="fixed inset-0 z-[9999] md:bg-black md:bg-opacity-50 md:flex md:items-start md:justify-center md:p-4 md:pt-16"
        onClick={handleClosePicker}
      >
        <div
          className="bg-white w-full h-[100dvh] flex flex-col overflow-hidden md:rounded-2xl md:shadow-2xl md:max-w-2xl md:h-[calc(100dvh-8rem)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Dialog Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-900">Localisation</h3>
            <button
              onClick={handleClosePicker}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Location Picker */}
          <div className="flex-1 relative">
            <OuigooLocationPicker
              apiKey={GOOGLE_MAPS_API_KEY}
              theme="ouigoo"
              language="fr"
              country={isoByPrefix[selectedCountryCode]}
              centerOnUserLocation
              enableSatelliteView
              enableMyLocation
              enableSearch
              confirmButtonStyle="rectangle"
              onLocationSelect={handleLocationSelect}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (businessError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-bold text-gray-900">Business indisponible</h1>
          <p className="text-gray-600">{businessError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-3 rounded-lg bg-black text-white font-semibold hover:bg-gray-800 transition"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (isBusinessLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-700">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin"></div>
          <span>Chargement…</span>
        </div>
      </div>
    );
  }

  // Thank you view
  if (orderResult) {
    const trackingId = orderResult.trackingID || orderResult.trackingId;
    const trackingLink = trackingId ? `/tracking?tracking-id=${trackingId}&submit=1` : null;

    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-xl w-full bg-white shadow-md rounded-2xl p-8 border border-gray-100">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-2xl">✓</div>
            <h1 className="text-2xl font-bold text-gray-900">Commande enregistrée</h1>
            <p className="text-gray-600">Merci ! Votre demande a été prise en compte.</p>
            {trackingId && (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <p className="text-sm text-gray-500">Tracking ID</p>
                <p className="text-lg font-semibold text-gray-900">{trackingId}</p>
              </div>
            )}
            {trackingLink && (
              <a
                href={trackingLink}
                className="inline-flex items-center justify-center px-4 py-3 rounded-lg app-btn text-white font-semibold transition"
              >
                Suivre mon colis
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white px-6 py-5 shadow-sm border-b sticky top-0 z-10 flex justify-center items-center gap-2">
        <img src="/res/logo.png" alt="Ouigoo" className="h-8" onError={(e) => e.target.style.display='none'} />
        <span className="text-xl font-bold text-gray-900 tracking-tight">{businessName}</span>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="space-y-4 mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            Passer une commande
          </h1>
          <p className="text-gray-600 text-lg">
            Remplissez le formulaire ci-dessous pour passer votre commande.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {hasPrefill && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-100 border border-gray-200 text-sm">
              <span className="text-gray-700">Vos informations ont été pré-remplies depuis votre dernière commande.</span>
              <button
                type="button"
                onClick={clearPrefill}
                className="text-gray-900 font-medium hover:underline shrink-0"
              >
                Effacer
              </button>
            </div>
          )}
          {/* Section Contact */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations de contact</h2>
            <div>
              <input
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                placeholder="Votre nom complet"
                className={`w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:bg-white transition-all ${
                  errors.customer_name ? 'focus:ring-red-500 border-red-300' : 'focus:ring-black'
                }`}
              />
              {errors.customer_name && (
                <p className="mt-1 text-sm text-red-600">{errors.customer_name}</p>
              )}
            </div>

            <div>
              <div className="flex gap-3">
                <div className="w-20 relative">
                  <select
                    value={selectedCountryCode}
                    onChange={handleCountryChange}
                    className="w-full bg-gray-100 rounded-lg px-3 py-3 text-transparent focus:outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all appearance-none cursor-pointer"
                  >
                    {countryOptions.map((opt) => (
                      <option key={opt.code} value={opt.code}>{opt.label} ({opt.code})</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-900 font-medium">{selectedCountryCode}</div>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">▾</div>
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneInput}
                  onBlur={() => { if (formData.phone) captureLead(); }}
                  placeholder="Numéro de téléphone"
                  className={`flex-1 bg-gray-100 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:bg-white transition-all ${
                    errors.phone ? 'focus:ring-red-500 border-red-300' : 'focus:ring-black'
                  }`}
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>
          </div>

          {/* Section Livraison */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Détails de livraison</h2>
            
            <div>
              <div className="relative">
                <input
                  type="text"
                  value={formData.address || ''}
                  onClick={handleLocationClick}
                  placeholder="Adresse de livraison"
                  className={`w-full bg-gray-100 rounded-lg pl-4 pr-12 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:bg-white transition-all cursor-pointer ${
                    errors.address ? 'focus:ring-red-500 border-red-300' : 'focus:ring-black'
                  }`}
                  readOnly
                />
                <button
                  type="button"
                  onClick={handleLocationClick}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-transform hover:scale-110 cursor-pointer"
                  title="Sélectionner sur la carte"
                >
                  <FaLocationArrow className="w-4 h-4 text-gray-800" />
                </button>
              </div>
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            <div>
              <input
                type="text"
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                placeholder="Plus de détails sur l'adresse"
                className={`w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:bg-white transition-all ${errors.instructions ? 'focus:ring-red-500' : 'focus:ring-black'}`}
              />
              {errors.instructions && <p className="mt-1 text-sm text-red-600">{errors.instructions}</p>}
            </div>

            <div>
              <input
                type="text"
                name="articles"
                value={formData.articles}
                onChange={handleChange}
                placeholder="Que souhaitez-vous commander ?"
                className={`w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:bg-white transition-all ${errors.articles ? 'focus:ring-red-500' : 'focus:ring-black'}`}
              />
              {errors.articles && <p className="mt-1 text-sm text-red-600">{errors.articles}</p>}
            </div>

            <div>
              {formConfig?.commentPrompt && (
                <label className="block text-sm font-medium text-gray-800 mb-1.5">
                  {formConfig.commentPrompt}
                  {formConfig?.commentRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}

              {/* Messaging-style composer: textarea + attached photos + attach toolbar share one surface */}
              <div
                className={`bg-gray-100 rounded-lg overflow-hidden transition-all focus-within:ring-2 focus-within:bg-white ${(errors.comment || errors.images) ? 'focus-within:ring-red-500' : 'focus-within:ring-black'}`}
              >
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  placeholder={formConfig?.commentRequired
                    ? 'Laissez un commentaire pour le marchand'
                    : 'Laissez un commentaire pour le marchand (facultatif)'}
                  rows={3}
                  className="w-full bg-transparent px-4 pt-3 pb-2 text-gray-900 placeholder-gray-500 focus:outline-none resize-none"
                />

                {formConfig?.imagesEnabled && (images.length > 0 || uploadingCount > 0) && (
                  <div className="flex flex-wrap gap-2 px-3 pb-2">
                    {images.map((url) => (
                      <div key={url} className="relative w-[60px] h-[60px] rounded-lg overflow-hidden bg-gray-200">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(url)}
                          className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center text-xs leading-none hover:bg-black/80"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {Array.from({ length: uploadingCount }).map((_, i) => (
                      <div key={`up-${i}`} className="w-[60px] h-[60px] rounded-lg bg-gray-200 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                      </div>
                    ))}
                  </div>
                )}

                {formConfig?.imagesEnabled && (
                  <div className="flex items-center justify-between px-2 py-1.5 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={images.length + uploadingCount >= MAX_ORDER_IMAGES}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-200/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <span className="text-sm font-medium">
                        Ajouter une image
                        {formConfig?.imagesRequired && <span className="text-red-500 ml-1">*</span>}
                      </span>
                    </button>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesSelected}
                  className="hidden"
                />
              </div>
              {errors.comment && <p className="mt-1 text-sm text-red-600">{errors.comment}</p>}
              {errors.images && <p className="mt-1 text-sm text-red-600">{errors.images}</p>}
            </div>
          </div>

          {/* Section Timing */}
          {/* <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quand voulez-vous être livré ?</h2>
            <div className="flex p-1 bg-gray-100 rounded-lg mb-4 w-fit">
              <button
                type="button"
                onClick={() => setDeliveryOption('asap')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  deliveryOption === 'asap'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Dès que possible
              </button>
              <button
                type="button"
                onClick={() => setDeliveryOption('scheduled')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  deliveryOption === 'scheduled'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Plus tard
              </button>
            </div>

            {deliveryOption === 'scheduled' && (
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="date"
                  name="delivery_date"
                  value={formData.delivery_date}
                  onChange={handleChange}
                  className="w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all"
                />
                <input
                  type="time"
                  name="delivery_time"
                  value={formData.delivery_time}
                  onChange={handleChange}
                  className="w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all"
                />
              </div>
            )}
          </div> */}

          {/* Estimation livraison */}
          {isEstimating && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin shrink-0"></div>
              <span className="text-sm text-gray-500">Calcul de l'estimation en cours…</span>
            </div>
          )}
          {estimation && !isEstimating && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700">Frais de livraison</p>
              <p className="text-lg font-bold text-gray-900">{estimation.tariff ?? '-'} FCFA</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || uploadingCount > 0}
            className="w-full app-btn text-white font-bold py-4 rounded-lg transition-all flex items-center justify-center active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : uploadingCount > 0 ? (
              <span>Téléversement des images…</span>
            ) : (
              <span>Confirmer la commande</span>
            )}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-gray-500">
          En passant commande, vous acceptez nos conditions générales d'utilisation.
        </p>
      </div>
    </div>
  );
};

export default PublicOrderForm;
